import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ShoppingCart, Heart, Truck, Shield, 
  Star, Package, Check, Clock, Award, 
  ChevronRight, ArrowLeft, Plus, Minus, 
 
} from 'lucide-react';
import { useAddToCart } from '../hooks/useAddToCart';
import AddonModal from '../components/products/AddonModal';
import ProductCard from '../components/products/ProductCard';
import dataService from '../services/api';

const ProductDetail = () => {
  const { type, id } = useParams();
  
  // State
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [comboItems, setComboItems] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [currentImage, setCurrentImage] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
 
  
  // Hooks
  const { showAddonModal, setShowAddonModal, handleAddToCart, handleAddWithAddons } = useAddToCart();

  useEffect(() => {
    fetchProductData();
  }, [type, id]);

  const fetchProductData = async () => {
    try {
      setLoading(true);
      let productData;
      
      if (type === 'combo') {
        const combos = await dataService.getCombos();
        productData = combos.find(c => c.id === parseInt(id));
      } else {
        const products = await dataService.getProducts();
        productData = products.find(p => p.id === parseInt(id));
      }
      
      setProduct(productData);
      
      if (productData?.category) {
        const allProducts = await dataService.getProducts();
        const related = allProducts
          .filter(p => p.category === productData.category && p.id !== productData.id)
          .slice(0, 4);
        setRelatedProducts(related);
      }
      
      if (type === 'combo' && productData?.items) {
        const allProducts = await dataService.getProducts();
        const items = productData.items
          .map(itemId => allProducts.find(p => p.id === itemId))
          .filter(Boolean);
        setComboItems(items);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = () => {
    if (!product) return 0;
    let price = product.price || 0;
    if (selectedOption) {
      const key = Object.keys(selectedOption)[0];
      price += selectedOption[key];
    }
    return price;
  };

  const handleAddToCartClick = () => {
    if (!product) return;

    const productToAdd = {
      ...product,
      type,
      quantity,
      customMessage: '',
      selectedOption: selectedOption,  
      finalPrice: calculatePrice()
    };
 
    handleAddToCart(productToAdd, true);
  };

  const incrementQuantity = () => setQuantity(prev => prev + 1);
  const decrementQuantity = () => setQuantity(prev => Math.max(1, prev - 1));

  const renderBriefPriceOptions = () => {
    if (!product?.brifPrize || product.brifPrize.length === 0) return null;

    return (
      <div className="mt-3">
        <h3 className="text-sm font-semibold mb-2 text-gray-900">Customization Options</h3>
        <div className="space-y-2">
          {product.brifPrize.map((option, index) => {
            const key = Object.keys(option)[0];
            const value = option[key];
            const isSelected = selectedOption === option;
            
            return (
              <button
                key={index}
                onClick={() => setSelectedOption(isSelected ? null : option)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  isSelected 
                    ? 'border-pink-500 bg-pink-50' 
                    : 'border-gray-200 hover:border-pink-300'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-medium text-sm text-gray-900">{key}</div>
                    <div className="text-xs text-gray-600 mt-0.5">Additional customization</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-pink-600 font-bold text-sm">+₹{value}</span>
                    {isSelected && (
                      <Check className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 pt-4 pb-8">
        <div className="container mx-auto px-3">
          {/* Loading skeleton */}
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="grid grid-cols-1 gap-4">
              <div className="h-64 bg-gray-200 rounded-lg"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                <div className="h-8 bg-gray-200 rounded w-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 flex items-center justify-center py-8 px-4">
        <div className="text-center max-w-sm">
          <h2 className="text-xl font-bold mb-3 text-gray-900">Product Not Found</h2>
          <Link 
            to="/shop" 
            className="inline-flex items-center justify-center bg-pink-600 text-white px-5 py-2.5 rounded-full font-medium hover:bg-pink-700 text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  const images = product.images || [product.image];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 pt-4 pb-8">
      {/* Header */}
      <div className="container mx-auto px-3 mb-4">
        <Link 
          to="/shop" 
          className="inline-flex items-center text-pink-600 hover:text-pink-700 text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back to Shop
        </Link>
      </div>

      <div className="container mx-auto px-3">
        <div className="grid grid-cols-1 gap-6">
          {/* Image Section */}
          <div className="space-y-3">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <img
                src={images[currentImage]}
                alt={product.name}
                className="w-full h-64 sm:h-80 object-contain"
              />
            </div>
            
            <div className="flex space-x-2 overflow-x-auto pb-2">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImage(index)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                    currentImage === index ? 'border-pink-500' : 'border-gray-200'
                  }`}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-contain"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-4">
            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              {type === 'combo' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-purple-600 text-xs font-semibold">
                  COMBO
                </span>
              )}
              {product.discount && (
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-600 text-xs font-semibold">
                  {product.discount}% OFF
                </span>
              )}
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                product.inStock ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
              }`}>
                {product.inStock ? 'IN STOCK' : 'OUT OF STOCK'}
              </span>
            </div>

            {/* Product Name */}
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
              {product.name}
            </h1>

            {/* Ratings */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <div className="flex">
                  {[1,2,3,4,5].map((star) => (
                    <Star 
                      key={star}
                      className={`h-4 w-4 ${
                        star <= 4.5 ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="ml-2 text-gray-600 text-xs">4.5 (128)</span>
              </div>
              <div className="flex items-center text-gray-500 text-xs">
                <Truck className="h-3.5 w-3.5 mr-1" />
                Free shipping
              </div>
            </div>

            {/* Price */}
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                {product.originalPrice && (
                  <span className="text-lg text-gray-400 line-through">
                    ₹{product.originalPrice}
                  </span>
                )}
                <span className="text-2xl font-bold text-pink-600">
                  ₹{calculatePrice()}
                </span>
                {product.originalPrice && (
                  <span className="text-green-600 font-semibold text-sm">
                    Save ₹{product.originalPrice - product.price}
                  </span>
                )}
              </div>
              
              {selectedOption && (
                <div className="text-xs text-gray-600">
                  Base price: ₹{product.price} + Customization: ₹{selectedOption[Object.keys(selectedOption)[0]]}
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <h3 className="text-sm font-semibold mb-1.5 text-gray-900">Description</h3>
              <p className="text-gray-700 text-sm leading-relaxed">{product.description}</p>
            </div>

            {/* BriefPrice Options */}
            {renderBriefPriceOptions()}

            {/* Combo Items */}
            {type === 'combo' && comboItems.length > 0 && (
              <div className="bg-purple-50 rounded-lg p-3">
                <h3 className="text-sm font-semibold mb-2 text-purple-900">
                  This Combo Includes:
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {comboItems.map(item => (
                    <div key={item.id} className="flex items-center gap-2 p-2 bg-white rounded-lg">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-10 h-10 object-contain rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-xs truncate">{item.name}</p>
                        <p className="text-pink-600 font-bold text-xs">₹{item.price}</p>
                      </div>
                      <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity & Actions */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <button
                    onClick={decrementQuantity}
                    className="px-3 py-2 hover:bg-gray-100 text-gray-700"
                    disabled={quantity <= 1}
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="px-4 py-2 font-semibold text-gray-900 min-w-[40px] text-center text-sm">
                    {quantity}
                  </span>
                  <button
                    onClick={incrementQuantity}
                    className="px-3 py-2 hover:bg-gray-100 text-gray-700"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>

                <button
                  onClick={handleAddToCartClick}
                  disabled={!product.inStock}
                  className="flex-1 flex items-center justify-center bg-pink-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Add to Cart - ₹{calculatePrice() * quantity}
                </button>
              </div>

              {/* <button
              
              onClick={()=>{


    if (!product) return;

    const productToAdd = {
      ...product,
      type,
      quantity,
      customMessage: '',
      selectedOption: selectedOption,  
      finalPrice: calculatePrice()
    };
 
   handleAddWithAddons(productToAdd)

    window.location.href='/cart'


              }}
              
              className="w-full flex items-center justify-center border border-pink-600 text-pink-600 px-4 py-2.5 rounded-lg font-medium hover:bg-pink-50 text-sm">
                <Heart className="h-4 w-4 mr-2" />
                Buy Now
              </button>*/}
            </div> 

            {/* Features */}
            <div className="grid grid-cols-2 gap-3 pt-4 border-t">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-pink-600" />
                <span className="text-xs">Quality Guaranteed</span>
              </div>
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-pink-600" />
                <span className="text-xs">Free Gift Wrap</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-pink-600" />
                <span className="text-xs">Same Day Delivery</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 text-pink-600" />
                <span className="text-xs">Premium Quality</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'details'
                  ? 'border-b-2 border-pink-600 text-pink-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('delivery')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'delivery'
                  ? 'border-b-2 border-pink-600 text-pink-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Delivery
            </button>
          </div>

          <div className="bg-white rounded-b-lg rounded-tr-lg p-4">
            {activeTab === 'details' ? (
              <div>
                <h3 className="text-base font-bold mb-3">About This Product</h3>
                <p className="text-gray-700 text-sm mb-4">{product.description}</p>
                {product.features && (
                  <>
                    <h4 className="text-sm font-semibold mb-2">Features:</h4>
                    <ul className="space-y-1.5">
                      {product.features.map((feature, index) => (
                        <li key={index} className="flex items-start text-sm">
                          <Check className="h-3.5 w-3.5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold mb-1.5">Delivery Areas</h4>
                  <p className="text-gray-700 text-sm">
                    We deliver across 100+ cities in India.
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold mb-1.5">Delivery Time</h4>
                  <ul className="space-y-1.5">
                    <li className="flex items-center text-sm">
                      <Clock className="h-3.5 w-3.5 text-green-500 mr-2" />
                      Same-day delivery
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-semibold mb-1.5">Shipping Charges</h4>
                  <p className="text-gray-700 text-sm">
                    Free shipping on orders above ₹999.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base sm:text-lg font-bold text-gray-900">Related Products</h2>
              <Link 
                to={`/shop?category=${product.category}`}
                className="text-pink-600 hover:text-pink-700 font-medium text-sm flex items-center"
              >
                View All <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
              </Link>
            </div>
            
            <div className="flex overflow-x-auto pb-4 -mx-3 px-3 scrollbar-hide">
              <div className="flex space-x-3 min-w-max">
                {relatedProducts.map(related => (
                  <div key={related.id} className="w-40 flex-shrink-0">
                    <ProductCard 
                      product={related} 
                      type="product"
                      compact={true}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Addon Modal */}
      <AddonModal
        isOpen={showAddonModal}
        onClose={() => setShowAddonModal(false)}
        selectedProduct={product ? { ...product, quantity, selectedOption } : null}
        onAddWithAddons={handleAddWithAddons}
      />
    </div>
  );
};

export default ProductDetail;